import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Lock, X, Clock, BarChart3, CheckCircle2 } from "lucide-react";
import { Reveal, SectionTag } from "@/components/Reveal";
import { api, useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const FILTERS = ["All", "Free", "Members"];

export default function Plans() {
  const { user, openAuthModal } = useAuth();
  const [plans, setPlans] = useState([]);
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  const openPlan = async (plan) => {
    if (plan.locked && !user) {
      openAuthModal(`"${plan.title}" is a member blueprint. Log in or create a free account to unlock it.`);
      return;
    }
    try {
      const { data } = await api.get(`/plans/${plan.slug}`);
      setActive(data);
    } catch (e) {
      if (e.response?.status === 401) {
        openAuthModal(`"${plan.title}" is a member blueprint. Log in or create a free account to unlock it.`);
      } else if (e.response?.status === 403) {
        toast.info("Vault access pending", { description: "Pay Theo in person (cash, Zelle, or Venmo), and he'll unlock the full blueprint vault for your account from his dashboard.", duration: 7000 });
      }
    }
  };

  const visible = plans.filter((p) => filter === "All" || (filter === "Free" ? !p.locked : p.locked));

  return (
    <main className="pt-16">
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <Reveal>
          <SectionTag index="SEC.01" label="The Vault" />
          <h1 data-testid="plans-heading" className="font-display font-black uppercase tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl">
            Training <span className="text-court-amber">blueprints.</span>
          </h1>
          <p className="mt-5 text-base md:text-lg text-zinc-400 max-w-2xl leading-relaxed">
            The exact workout and drill plans Theo uses in private sessions. Two free previews for everyone — create a free account and Theo unlocks the full library after you pay in person.
          </p>
        </Reveal>

        <div className="mt-10 flex gap-2">
          {FILTERS.map((f) => (
            <button key={f} data-testid={`plan-filter-${f.toLowerCase()}`} onClick={() => setFilter(f)}
              className={`px-5 py-2 rounded-full font-mono text-xs uppercase tracking-widest transition-colors ${filter === f ? "bg-court-amber text-court-void font-bold" : "bg-white/5 text-zinc-400 hover:text-white border border-white/10"}`}>
              {f}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="mt-16 text-center font-mono text-xs uppercase tracking-widest text-zinc-500">Loading blueprints...</p>
        ) : (
          <div data-testid="plans-grid" className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {visible.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.05}>
                <button data-testid={`plan-card-${p.slug}`} onClick={() => openPlan(p)}
                  className="card-lift group text-left w-full bg-court-surface/80 border border-white/10 rounded-2xl overflow-hidden">
                  <div className="relative h-44 overflow-hidden">
                    <img src={p.image} alt={p.title} className="gallery-img w-full h-full object-cover" />
                    <span className={`absolute top-3 right-3 font-mono text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full flex items-center gap-1.5 ${p.locked ? "bg-court-void/90 text-court-amber border border-court-amber/40" : "bg-court-amber text-court-void"}`}>
                      {p.locked && <Lock size={11} />}{p.locked ? "Members" : "Free"}
                    </span>
                    {p.locked && (
                      <div className="absolute inset-0 bg-court-void/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <Lock size={28} className="text-court-amber" />
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                      <span className="text-court-amber">{p.category}</span><span>•</span><span>{p.level}</span><span>•</span><span>{p.duration}</span>
                    </div>
                    <h3 className="font-display font-bold uppercase tracking-tight text-xl sm:text-2xl mt-2">{p.title}</h3>
                    <p className="text-sm text-zinc-400 mt-2 line-clamp-2">{p.summary}</p>
                    <div className="mt-3 font-mono text-xs uppercase tracking-widest text-zinc-500">{p.drill_count} drills</div>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        )}
      </section>

      {/* PLAN MODAL */}
      <AnimatePresence>
        {active && (
          <motion.div data-testid="plan-modal" className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setActive(null)}>
            <motion.div
              className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto bg-court-surface border border-white/10 rounded-2xl shadow-2xl"
              initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative h-52">
                <img src={active.image} alt={active.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-court-surface to-transparent" />
                <button data-testid="plan-modal-close" onClick={() => setActive(null)} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-court-void/80 border border-white/20 flex items-center justify-center text-zinc-300 hover:text-white transition-colors">
                  <X size={17} />
                </button>
                <div className="absolute bottom-4 left-6 right-6">
                  <h3 className="font-display font-extrabold uppercase tracking-tight text-2xl sm:text-3xl">{active.title}</h3>
                </div>
              </div>
              <div className="p-6 sm:p-8">
                <div className="flex flex-wrap gap-4 font-mono text-xs uppercase tracking-widest text-zinc-400">
                  <span className="flex items-center gap-1.5"><BarChart3 size={13} className="text-court-amber" />{active.level}</span>
                  <span className="flex items-center gap-1.5"><Clock size={13} className="text-court-amber" />{active.duration}</span>
                  <span className="text-court-amber">{active.category}</span>
                </div>
                <p className="mt-4 text-sm sm:text-base text-zinc-300 leading-relaxed">{active.summary}</p>
                <div className="mt-7 space-y-4">
                  {active.drills.map((d, i) => (
                    <div key={d.name} data-testid={`plan-drill-${i}`} className="flex gap-4 bg-court-void/60 border border-white/10 rounded-xl p-4">
                      <span className="font-display font-black text-2xl text-outline shrink-0">0{i + 1}</span>
                      <div>
                        <div className="flex flex-wrap items-center gap-3">
                          <h4 className="font-display font-bold uppercase tracking-wide text-lg">{d.name}</h4>
                          <span className="font-mono text-[10px] uppercase tracking-widest text-court-amber bg-court-amber/10 border border-court-amber/30 rounded-full px-2.5 py-0.5">{d.sets}</span>
                        </div>
                        <p className="text-sm text-zinc-400 mt-1.5 leading-relaxed">{d.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-7 flex items-center gap-3 bg-court-amber/10 border border-court-amber/30 rounded-xl px-5 py-4">
                  <CheckCircle2 size={17} className="text-court-amber shrink-0" />
                  <p className="text-xs text-zinc-300">Bring this blueprint to your next session and Theo will run it with you live.</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
